import { imgixHost, isConsumer, isKiosk, isPos } from 'src/config'
import ActivityCategory from 'src/models/activity/ActivityCategory'
import Generic from 'src/models/generic/Generic'
import Model, { ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Librariable from 'src/models/model/concerns/Librariable'
import ItemPackage from 'src/models/package/ItemPackage'
import PriceCard from 'src/models/showing/PriceCard'
import TicketType from 'src/models/showing/TicketType'

export const SESSION_ADMISSION_TYPES = {
  SESSION: 'session',
  ACTIVITY_UNIT_FIXED: 'activity_unit_fixed',
  ACTIVITY_UNIT_DYNAMIC: 'activity_unit_dynamic'
}

export function friendlySessionAdmissionType (sessionAdmissionType) {
  switch (sessionAdmissionType) {
    case SESSION_ADMISSION_TYPES.SESSION:
      return 'Fixed Start & End Time'
    case SESSION_ADMISSION_TYPES.ACTIVITY_UNIT_FIXED:
      return 'Fixed Block of Time'
    case SESSION_ADMISSION_TYPES.ACTIVITY_UNIT_DYNAMIC:
      return 'Dynamic Block of Time'
  }
}

SESSION_ADMISSION_TYPES.ALL = Object.values(SESSION_ADMISSION_TYPES)

export default class Activity extends Model {
  static name = 'Activity'

  static versioned = true
  static isConfiguration = true
  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.NonFilmProgramming]

  static define () {
    this.concerns = [Librariable]

    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      name: {
        type: String,
        default: null,
        sortable: true,
        translatable: true
      },
      urlSlug: {
        type: String,
        default: null,
        sortable: true
      },
      urlSlugIsReadonly: {
        type: Boolean,
        default: false,
        output: true,
        excludeFromDefault: true
      },
      description: {
        type: String,
        default: null,
        translatable: true
      },
      isExclusive: {
        type: Boolean,
        default: false
      },
      hasMinimumAttendance: {
        type: Boolean,
        default: false
      },
      minimumAttendance: {
        type: Number,
        default: 1
      },
      sessionAdmissionType: {
        type: String,
        default: SESSION_ADMISSION_TYPES.SESSION,
        options: SESSION_ADMISSION_TYPES.ALL
      },
      sessionDurationMinutes: {
        type: Number,
        default: 30
      },
      sessionBufferMinutes: {
        type: Number,
        default: 15
      },
      posterImage: {
        type: String,
        default: null,
        sortable: true,
        translatable: true
      },
      posterImageParams: {
        type: String,
        default: 'fit=crop&w=400&h=600&fm=jpeg&auto=format,compress',
        local: true
      },
      posterImageSmallParams: {
        type: String,
        default: 'fit=crop&w=60&h=90&fm=jpeg&auto=format,compress',
        local: true
      },
      bannerImage: {
        type: String,
        default: null,
        sortable: true,
        translatable: true
      },
      bannerImageParams: {
        type: String,
        default: 'fit=crop&w=1000&height=500&fm=jpeg&auto=format,compress',
        local: true
      },
      bannerImageSmallParams: {
        type: String,
        default: 'fit=crop&w=120&h=60&fm=jpeg&auto=format,compress',
        local: true
      },
      videoPlatform: {
        type: String,
        default: 'youtube',
        options: ['youtube', 'vimeo']
      },
      videoId: {
        type: String,
        default: null,
        translatable: true
      },
      color: {
        type: String,
        sortable: true
      },
      entryIntervalMinutes: {
        type: Number,
        default: 5
      },
      activityUnitDescription: {
        type: String,
        default: 'Game',
        translatable: true
      },
      activityUnitDescriptionPlural: {
        type: String,
        output: true
      },
      activityUnitDurationMinutes: {
        type: Number,
        default: 15
      },
      maxConsecutiveActivityUnits: {
        type: Number,
        default: 1
      },
      defaultPriceCards: {
        type: JsonObject,
        default: () => ({})
      },
      displayOrder: {
        type: Integer,
        default: 999
      },
      waitlistOptions: {
        type: JsonObject,
        default () {
          return {
            allow_waitlist_activity_bookings: false,
            collect_name: false,
            name_required: false,
            collect_email: false,
            email_required: false,
            collect_phone: false,
            phone_required: false
          }
        },
        mergeDefault: true
      },
      activityCategories: {
        type: [ActivityCategory],
        default: () => ([]),
        acceptsNestedAttributes: true
      },
      ticketTypes: {
        type: [TicketType],
        reference: true,
        output: true
      },
      itemPackages: {
        type: [ItemPackage],
        reference: true,
        output: true
      },
      fromPrices: {
        type: JsonObject,
        excludeFromDefault: true,
        output: true
      },
      maxActivitySpaceCapacity: {
        type: Integer,
        excludeFromDefault: true,
        output: true
      },
      localPriceCard: {
        type: PriceCard,
        input: true
      }
    }

    this.computed = {
      posterImageUrl () {
        if (!this.posterImage) return ''

        return `${imgixHost}/${this.posterImage}?${this.posterImageParams}`
      },
      posterImageSmallUrl () {
        if (!this.posterImage) return ''

        return `${imgixHost}/${this.posterImage}?${this.posterImageSmallParams}`
      },
      bannerImageUrl () {
        if (!this.bannerImage) return

        return `${imgixHost}/${this.bannerImage}?${this.bannerImageParams}`
      },
      bannerImageSmallUrl () {
        if (!this.bannerImage) return ''

        return `${imgixHost}/${this.bannerImage}?${this.bannerImageSmallParams}`
      },
      descriptionWithBreaks () {
        return this.description?.replace(/\n/g, '<br>') || ''
      },
      allTicketTypes () {
        return this.filterTicketTypes()
      },
      guestTicketTypes () {
        return this.filterTicketTypes('guest')
      },
      allItemPackages () {
        return this.filterItemPackages()
      },
      guestItemPackages () {
        return this.filterItemPackages('guest')
      },
      videoSource () {
        if (this.videoPlatform === 'vimeo') {
          return 'https://player.vimeo.com/video/' + this.videoId
        } else {
          return 'https://www.youtube.com/embed/' + this.videoId
        }
      }
    }

    this.queries = {
      findActivityBySlug: {
        arguments: {
          urlSlug: {
            type: String,
            required: true
          }
        },
        results: {
          activity: Activity
        }
      },
      datesWithActivities: {
        results: {
          datesWithActivities: Generic
        }
      }
    }

    this.paginate = true
  }

  // TODO OOS INDY-6757 Move all of this logic out of the Model and into a Composable which can validate Stock and other requirements
  filterTicketTypes (mode = 'all', member = null) {
    return _(this.ticketTypes.data)
      .filter(ticketType => {
        if (this.localPriceCard) {
          /**
           * Filter for a specific price card
           */
          return _(this.localPriceCard.priceCardItems.data).map('ticketType.id').compact().includes(ticketType.id)
        } else {
          return true
        }
      })
      .filter(ticketType => {
        /**
         * Filter based on mode and appMode
         */
        return (
          ticketType.isAvailable &&
          (mode !== 'guest' || !ticketType.membershipType) &&
          (mode !== 'member' || (
            (!ticketType.membershipType || ticketType.isMembershipTypeMatch(member.effectiveMembershipType?.id)) &&
            (!ticketType.userClass || ticketType.isUserClassMatch(member.userClass?.id))
          ))
        )
      })
      .value()
  }

  memberTicketTypes (member) {
    return this.filterTicketTypes('member', member)
  }

  filterItemPackages (mode = 'all', member = null) {
    return _(this.itemPackages.data)
      .filter(itemPackage => {
        if (this.localPriceCard) {
          /**
           * Filter for a specific price card
           */
          return _(this.localPriceCard.priceCardItems.data).map('itemPackage.id').compact().includes(itemPackage.id)
        } else {
          return true
        }
      })
      .filter(itemPackage => {
        /**
         * Filter based on mode and appMode
         */
        const itemPackageTicketTypes = _(itemPackage.itemPackageItems.data).map(packItem => packItem.ticketType).compact().value()
        const itemPackageContainsMembership = _.some(itemPackageTicketTypes, ticketType => !!ticketType.membershipType)

        if (isPos && !itemPackage.salesChannels.register) return false
        if (isConsumer && !itemPackage.salesChannels.website) return false
        if (isKiosk && !itemPackage.salesChannels.kiosk) return false

        return (
          (mode !== 'guest' || !itemPackageContainsMembership) &&
          (mode !== 'member' ||
            (member && _.every(itemPackageTicketTypes, ticketType => (
              (!ticketType.membershipType || ticketType.isMembershipTypeMatch(member.effectiveMembershipType?.id)) &&
              (!ticketType.userClass || ticketType.isUserClassMatch(member.userClass?.id))
            )))
          )
        )
      })
      .value()
  }

  memberItemPackages (member) {
    return this.filterItemPackages('member', member)
  }

  get allowWaitlistActivityBookings () {
    return this.waitlistOptions.allow_waitlist_activity_bookings
  }

  set allowWaitlistActivityBookings (value) {
    this.waitlistOptions.allow_waitlist_activity_bookings = value
  }

  get collectName () {
    return this.waitlistOptions.collect_name
  }

  set collectName (value) {
    this.waitlistOptions.collect_name = value
  }

  get nameRequired () {
    return this.waitlistOptions.name_required
  }

  set nameRequired (value) {
    this.waitlistOptions.name_required = value
  }

  get collectEmail () {
    return this.waitlistOptions.collect_email
  }

  set collectEmail (value) {
    this.waitlistOptions.collect_email = value
  }

  get emailRequired () {
    return this.waitlistOptions.email_required
  }

  set emailRequired (value) {
    this.waitlistOptions.email_required = value
  }

  get collectPhone () {
    return this.waitlistOptions.collect_phone
  }

  set collectPhone (value) {
    this.waitlistOptions.collect_phone = value
  }

  get phoneRequired () {
    return this.waitlistOptions.phone_required
  }

  set phoneRequired (value) {
    this.waitlistOptions.phone_required = value
  }
}
